import { useParams, Link } from 'react-router-dom';
import { useStream } from '../../hooks/useStreams';
import { ArrowLeft, Layers, FileText, Clock, ExternalLink } from 'lucide-react';

const orgPrefixFromSlug = (orgSlug?: string) => orgSlug ? `/${orgSlug}` : '';

const streamTypeColors = {
  event: 'bg-blue-100 text-blue-800',
  ongoing: 'bg-purple-100 text-purple-800',
  thematic: 'bg-green-100 text-green-800',
};

export function StreamPage() {
  const { id, orgSlug } = useParams<{ id: string; orgSlug: string }>();
  const orgPrefix = orgPrefixFromSlug(orgSlug);
  const { data, isLoading, error } = useStream(id || '');

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Failed to load stream
        </div>
      </div>
    );
  }

  const { stream, stories } = data;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back link */}
      <Link
        to={`${orgPrefix}/streams`}
        className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        All Streams
      </Link>

      {/* Stream header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Layers className="w-6 h-6 text-gray-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${streamTypeColors[stream.streamType as keyof typeof streamTypeColors] || 'bg-gray-100 text-gray-800'}`}>
                {stream.streamType}
              </span>
              {stream.startDate && (
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Started {new Date(stream.startDate).toLocaleDateString()}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{stream.title}</h1>
            {stream.description && (
              <p className="text-gray-600 mt-2">{stream.description}</p>
            )}
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              {stream.storyCount !== undefined && (
                <span className="flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  {stream.storyCount} {stream.storyCount === 1 ? 'story' : 'stories'}
                </span>
              )}
              {stream.claimCount !== undefined && <span>{stream.claimCount} claims</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Stories in this stream */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          Stories in this Stream
        </h2>

        {stories.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
            No stories in this stream yet.
          </div>
        ) : (
          <div className="space-y-4">
            {stories.map((story) => (
              <Link
                key={story.id}
                to={`${orgPrefix}/story/${story.id}`}
                className="block bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900">{story.title}</h3>
                    {story.summary && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{story.summary}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      {story.claimCount !== undefined && <span>{story.claimCount} claims</span>}
                      {story.materialCount !== undefined && <span>{story.materialCount} materials</span>}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(story.modifiedAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
